import React, { useCallback, useMemo, useState, type FC } from 'react';
import type { CouponItemParams, CouponListProps } from './coupon-list.types';
import type { SalesRule } from '../coupon.types';
import Icon from '../../../atoms/icon';
import { getColorVar } from '../../../../theme';
import { TextIcon } from '../../../molecules/text-icon';
import Button from '../../../atoms/button';
import Divider from '../../../atoms/divider';
import { FlatList } from '../../../molecules/flat-list';
import { cn } from '../../../utils/cn';
import { CouponItem } from '../coupon-item/coupon-item';
import styles from './coupon-list.module.css';

export const CouponList: FC<CouponListProps> = props => {
    const {
        section,
        coupons,
        collapsible,
        collapsedItemCount,
        defaultExpanded,
        selectedCoupon,
        onSelectCoupon,
        onCouponDetail,
        viewMoreText,
        collapseText,
        emptyText: _emptyText,
    } = props;

    const [expandedSections, setExpandedSections] = useState<Record<number, boolean>>({});

    const sectionIndex = useMemo(() => coupons.indexOf(section), [coupons, section]);

    const shouldCollapse = useMemo(
        () => collapsible && section.data.length > collapsedItemCount,
        [collapsible, section.data.length, collapsedItemCount]
    );
    const isExpanded = useMemo(
        () => expandedSections[sectionIndex] ?? defaultExpanded,
        [expandedSections, sectionIndex, defaultExpanded]
    );
    const visibleData = useMemo(
        () => (shouldCollapse && !isExpanded ? section.data.slice(0, collapsedItemCount) : section.data),
        [shouldCollapse, isExpanded, section.data, collapsedItemCount]
    );

    const handleTogglePress = useCallback((index: number) => {
        setExpandedSections(prev => ({
            ...prev,
            [index]: !prev[index],
        }));
    }, []);

    const handleCheckBoxPress = useCallback(
        (item: SalesRule) => {
            onSelectCoupon(item);
        },
        [onSelectCoupon]
    );

    const rightIconName = useMemo(() => (isExpanded ? 'chevron-up' : 'chevron-down'), [isExpanded]);

    const getRightIcon = useCallback(
        () => <Icon name={rightIconName} color={getColorVar('primary', '100')} containerClassName={styles.rightIcon} />,
        [rightIconName]
    );

    const keyExtractor = useCallback((item: SalesRule, index: number) => item.code ?? `coupon-${index}`, []);

    const renderCouponItem = useCallback(
        ({ item, index }: CouponItemParams) => (
            <CouponItem
                key={keyExtractor(item, index)}
                item={item}
                selectedItem={selectedCoupon}
                onItemSelected={handleCheckBoxPress}
                onCouponDetail={onCouponDetail}
                disabled={section.isDisabled}
                isLastItem={index === visibleData.length - 1}
            />
        ),
        [keyExtractor, selectedCoupon, handleCheckBoxPress, onCouponDetail, section.isDisabled, visibleData.length]
    );

    const renderHeader = useCallback(
        () => (
            <div className={styles.sectionHeader}>
                <TextIcon text={section.title} textClassName={styles.title} />
                <Divider className={styles.divider} />
            </div>
        ),
        [section.title]
    );

    const renderFooter = useCallback(
        () =>
            shouldCollapse ? (
                <div className={styles.footerContainer}>
                    <Button
                        testID={`coupon-list-view-more-button-${sectionIndex}`}
                        onPress={() => handleTogglePress(sectionIndex)}
                        className={styles.footerButton}
                    >
                        <TextIcon
                            containerClassName={styles.textIcon}
                            textTestId={`coupon-list-view-more-label-${sectionIndex}`}
                            fontWeight="bold"
                            text={isExpanded ? collapseText : viewMoreText}
                            fontColor={getColorVar('primary', '100')}
                            fontSize="small"
                            rightIcon={getRightIcon()}
                        />
                    </Button>
                </div>
            ) : null,
        [shouldCollapse, sectionIndex, handleTogglePress, isExpanded, collapseText, viewMoreText, getRightIcon]
    );

    const renderEmptyComponent = useCallback(() => {
        return null;
    }, []);

    return (
        <div className={cn(styles.sectionContainer, section?.isDisabled ? styles.sectionContainerDisable : null)}>
            <FlatList
                data={visibleData}
                keyExtractor={keyExtractor}
                renderItem={renderCouponItem}
                ListHeaderComponent={renderHeader}
                ListFooterComponent={renderFooter}
                initialNumToRender={collapsedItemCount}
                maxToRenderPerBatch={10}
                windowSize={5}
                ListEmptyComponent={renderEmptyComponent}
            />
        </div>
    );
};
